import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  Loader2, Briefcase, MapPin, CalendarRange, Clock, Users, AlertCircle, CheckCircle2, Building2,
  ChevronDown, Heart, ExternalLink, Compass,
} from 'lucide-react';
import { toast } from 'sonner';
import { ApplyDialog, ApplicationCard } from './InternshipApply';
import ReferralSection from './ParentReferral';
import { PlacementPanel } from './ParentPlacements';
import { refreshInternshipAlerts } from '@/lib/useInternshipAlerts';
import { journeyStage, stageRank, isHistory, STAGE_LABEL, STAGE_TONE } from '@/lib/internshipJourney';

const fmtDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch { return iso; }
};
const FORMAT_LABELS = { on_site: 'On-site', remote: 'Remote', hybrid: 'Hybrid' };
const gradeList = (grades) => (grades || []).map(g => `Grade ${g}`).join(', ');
const gradeOf = (child) => {
  const n = parseInt(String(child?.grade ?? '').trim(), 10);
  return Number.isNaN(n) ? null : n;
};

export default function ParentInternships() {
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [applications, setApplications] = useState([]);
  const [placements, setPlacements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);
  const [applyOpen, setApplyOpen] = useState(false);
  const [prefs, setPrefs] = useState({ p1: '', p2: '' });
  const [showRefer, setShowRefer] = useState(false);

  const load = useCallback(async () => {
    try {
      const [r, ra, rp] = await Promise.all([
        api.get('/parent/internships'),
        api.get('/parent/internship-applications'),
        api.get('/parent/internship-placements'),
      ]);
      setData(r.data);
      setApplications(ra.data || []);
      setPlacements(rp.data || []);
    } catch {
      toast.error('Could not load internships. Please refresh.');
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const children = useMemo(() => data?.children || [], [data]);

  // every programme that matters to a child: open ones they are eligible for, plus their own history
  const programsFor = useCallback((child) => {
    if (!child) return [];
    const grade = gradeOf(child);
    const rows = (data?.programs || [])
      .filter(p => grade !== null && (p.eligible_grades || []).includes(grade))
      .map(p => ({ ...p, open: true }));
    const seen = new Set(rows.map(p => p.id));
    applications
      .filter(a => a.child_id === child.child_id && !seen.has(a.program_id))
      .forEach(a => {
        seen.add(a.program_id);
        rows.push({
          id: a.program_id, name: a.program_name, application_deadline: a.application_deadline,
          applications_open: false, open: false, eligible_grades: [],
        });
      });
    return rows.map(p => {
      const application = applications.find(a => a.child_id === child.child_id && a.program_id === p.id);
      const placement = placements.find(x => application && x.id === application.id);
      const stage = journeyStage({
        application, placement, program: p, eligible: grade !== null && (p.eligible_grades || []).includes(grade),
      });
      return { ...p, application, placement, stage, rank: stageRank(stage) };
    }).sort((a, b) => a.rank - b.rank
      || String(b.start_date || '').localeCompare(String(a.start_date || '')));
  }, [data, applications, placements]);

  const childRank = useCallback((child) => {
    const rows = programsFor(child);
    return rows.length ? rows[0].rank : 9;
  }, [programsFor]);

  const childId = params.get('child');
  const programId = params.get('program');
  const child = children.find(c => c.child_id === childId)
    || [...children].sort((a, b) => childRank(a) - childRank(b))[0];
  const programs = programsFor(child);
  const program = programs.find(p => p.id === programId) || programs[0];

  const select = (nextChild, nextProgram) => {
    const next = new URLSearchParams(params);
    next.set('child', nextChild);
    if (nextProgram) next.set('program', nextProgram); else next.delete('program');
    next.delete('placement');
    setParams(next, { replace: false });
  };

  const existingApp = program?.application;
  useEffect(() => {
    setPrefs({
      p1: existingApp?.priority1_opportunity_id || '',
      p2: existingApp?.priority2_opportunity_id || '',
    });
  }, [child?.child_id, program?.id, existingApp?.priority1_opportunity_id,
      existingApp?.priority2_opportunity_id]);

  const placementParam = params.get('placement');
  useEffect(() => {
    if (loading || !placementParam) return;
    document.querySelector(`[data-testid="placement-panel-${placementParam}"]`)
      ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [loading, placementParam]);

  if (loading) {
    return <div className="py-16 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#F97D6A]" /></div>;
  }

  if (!children.length) {
    return (
      <div className="rounded-2xl border border-dashed border-[#E5E7EB] p-10 text-center" data-testid="internships-empty">
        <Briefcase className="h-7 w-7 text-gray-200 mx-auto mb-3" />
        <p className="text-sm font-semibold text-[#1F2937] font-['Outfit'] mb-1">No student profile linked yet</p>
        <p className="text-sm text-[#9CA3AF]">Please contact your Career Coordinator to link your child's profile.</p>
      </div>
    );
  }

  // families only ever see what their selected child can actually apply to
  const opportunities = (data?.opportunities || []).filter(o => o.program_id === program?.id
    && (o.child_eligibility || []).some(c => c.child_id === child?.child_id && c.eligible));
  const selectedOpp = opportunities.find(o => o.id === openId);
  const childApplications = applications.filter(a => a.child_id === child?.child_id);
  // a closed programme nobody applied to is not part of the child's story
  const history = programs.filter(p => isHistory(p.stage) && p.id !== program?.id
    && (p.application || p.placement));

  return (
    <div data-testid="parent-internships">
      {/* ── who are we looking at ── */}
      {children.length > 1 && (
        <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="child-selector">
          {children.map(c => {
            const active = c.child_id === child?.child_id;
            const urgent = childRank(c) <= 1;
            return (
              <button
                key={c.child_id}
                onClick={() => select(c.child_id)}
                className={`rounded-full px-4 py-2 text-xs font-semibold transition-colors ${active ? 'bg-[#1F2937] text-white' : 'bg-[#F3F4F6] text-[#6B7280] hover:text-[#1F2937]'}`}
                data-testid={`child-tab-${c.child_id}`}
              >
                {c.child_name}
                {c.grade ? <span className={`ml-1.5 font-normal ${active ? 'text-white/60' : 'text-[#9CA3AF]'}`}>Grade {c.grade}</span> : null}
                {urgent && !active && <span className="text-[#F97D6A] font-bold ml-1">*</span>}
              </button>
            );
          })}
        </div>
      )}

      {programs.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-[#E5E7EB] p-10 text-center" data-testid="no-programs">
          <Briefcase className="h-7 w-7 text-gray-200 mx-auto mb-3" />
          <p className="text-sm font-semibold text-[#1F2937] font-['Outfit'] mb-1">
            No internship programme for {child?.child_name} right now
          </p>
          <p className="text-sm text-[#9CA3AF]">
            Your school opens programmes for selected grades — you will see them here as soon as one is open
            {child?.grade ? ` for grade ${child.grade}` : ''}.
          </p>
        </div>
      ) : (
        <>
          {/* ── which programme ── */}
          {programs.length > 1 && (
            <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="program-selector">
              {programs.map(p => {
                const active = p.id === program?.id;
                return (
                  <button
                    key={p.id}
                    onClick={() => select(child.child_id, p.id)}
                    className={`rounded-full border px-3.5 py-1.5 text-xs font-medium transition-colors ${active ? 'border-[#F97D6A] bg-[#FFF7F5] text-[#1F2937]' : 'border-[#E5E7EB] text-[#6B7280] hover:border-[#D1D5DB]'}`}
                    data-testid={`program-tab-${p.id}`}
                  >
                    {p.name}
                    <span className={`ml-2 text-[10px] ${active ? 'text-[#F06651]' : 'text-[#9CA3AF]'}`}>
                      {STAGE_LABEL[p.stage]}
                    </span>
                  </button>
                );
              })}
            </div>
          )}

          <JourneyPanel
            child={child}
            program={program}
            autoConfirm={params.get('confirm') === program.application?.id}
            onEditApplication={() => setApplyOpen(true)}
            onChanged={() => { load(); refreshInternshipAlerts(); }}
          />

          {/* ── opportunities for this programme ── */}
          {['applications_open', 'applied'].includes(program.stage) && (
            <section className="mt-7" data-testid="opportunities-section">
              <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                <h3 className="text-sm font-bold text-[#1F2937] font-['Outfit']">
                  Internships in {program.name}
                </h3>
                <p className="text-xs text-[#9CA3AF]" data-testid="internships-count">
                  {opportunities.length} open to {child.child_name.split(' ')[0]}
                </p>
              </div>
              {prefs.p1 && (
                <PreferenceSummary
                  prefs={prefs}
                  opportunities={opportunities}
                  onOpen={(id) => setOpenId(id)}
                  onRemove={(slot) => setPrefs(slot === 'p1'
                    ? { p1: prefs.p2, p2: '' }
                    : { ...prefs, p2: '' })}
                  onContinue={() => setApplyOpen(true)}
                />
              )}
              {opportunities.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-[#E5E7EB] p-8 text-center" data-testid="no-opportunities-yet">
                  <Briefcase className="h-6 w-6 text-gray-200 mx-auto mb-2.5" />
                  <p className="text-sm font-semibold text-[#1F2937] font-['Outfit']">
                    Nothing open to {child.child_name.split(' ')[0]} here yet
                  </p>
                  <p className="text-sm text-[#9CA3AF] mt-1 max-w-md mx-auto leading-relaxed">
                    No internship in this programme is open to {child.grade ? `grade ${child.grade}` : 'this grade'} so far.
                    Companies are still submitting offers — new ones appear here as soon as the school approves them,
                    and you are welcome to propose a company below.
                  </p>
                </div>
              ) : (
                <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
                  {opportunities.map(o => (
                    <OpportunityCard
                      key={o.id} opp={o} child={child}
                      preference={prefs.p1 === o.id ? 1 : prefs.p2 === o.id ? 2 : 0}
                      onOpen={() => setOpenId(o.id)}
                    />
                  ))}
                </div>
              )}
            </section>
          )}

          {/* ── earlier programmes, compact ── */}
          {history.length > 0 && (
            <section className="mt-8" data-testid="internship-history">
              <h3 className="text-sm font-bold text-[#1F2937] font-['Outfit'] mb-3">
                {child.child_name}'s earlier internships
              </h3>
              <div className="space-y-2">
                {history.map(p => <HistoryRow key={p.id} row={p} onOpen={() => select(child.child_id, p.id)} />)}
              </div>
            </section>
          )}
        </>
      )}

      {/* ── propose a company: secondary ── */}
      <section className="mt-8" data-testid="propose-section">
        <button
          onClick={() => setShowRefer(v => !v)}
          className="w-full flex items-center justify-between gap-3 rounded-2xl border border-[#E5E7EB] px-4 py-3 text-left hover:border-[#D1D5DB] transition-colors"
          data-testid="propose-toggle"
        >
          <span>
            <span className="text-sm font-semibold text-[#1F2937] font-['Outfit']">Propose an internship</span>
            <span className="block text-xs text-[#9CA3AF] mt-0.5">
              Know a company that could host students? Tell the school and your referral stays credited to you.
            </span>
          </span>
          <ChevronDown className={`h-4 w-4 text-[#9CA3AF] shrink-0 transition-transform ${showRefer ? 'rotate-180' : ''}`} />
        </button>
        {showRefer && (
          <div className="mt-3">
            <ReferralSection programs={(data?.programs || [])} refreshKey={applications.length} />
          </div>
        )}
      </section>

      <Dialog open={!!selectedOpp} onOpenChange={(v) => { if (!v) setOpenId(null); }}>
        <DialogContent className="sm:max-w-2xl rounded-2xl max-h-[88vh] overflow-y-auto" data-testid="internship-details-dialog">
          {selectedOpp && (
            <OpportunityDetails
              opp={selectedOpp} child={child}
              preference={prefs.p1 === selectedOpp.id ? 1 : prefs.p2 === selectedOpp.id ? 2 : 0}
              prefs={prefs}
              onSelect={() => {
                setPrefs(p => (p.p1 === selectedOpp.id ? { p1: p.p2, p2: '' }
                  : p.p2 === selectedOpp.id ? { ...p, p2: '' }
                  : !p.p1 ? { ...p, p1: selectedOpp.id }
                  : { ...p, p2: selectedOpp.id }));
                setOpenId(null);
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      {applyOpen && program && prefs.p1 && (
        <ApplyDialog
          open={applyOpen}
          onOpenChange={setApplyOpen}
          program={program}
          opportunities={opportunities}
          children={children.filter(c => c.child_id === child.child_id)}
          application={existingApp}
          childId={child.child_id}
          priority1={prefs.p1}
          priority2={prefs.p2}
          onDone={() => { load(); refreshInternshipAlerts(); }}
        />
      )}
    </div>
  );
}

/** The single status-driven story for one child in one programme. */
function JourneyPanel({ child, program, autoConfirm, onEditApplication, onChanged }) {
  const { stage, application, placement } = program;
  const tone = STAGE_TONE[stage] || 'bg-gray-100 text-gray-500';

  return (
    <div className="rounded-2xl border border-[#EFF0F2] bg-white p-5" data-testid={`journey-${child.child_id}-${program.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-wider">Internship programme</p>
          <h2 className="text-lg font-bold text-[#1F2937] font-['Outfit'] mt-0.5">{program.name}</h2>
          <div className="flex flex-wrap gap-x-5 gap-y-1 mt-1.5 text-sm text-[#6B7280]">
            {program.start_date && (
              <span className="inline-flex items-center gap-1.5"><CalendarRange className="h-3.5 w-3.5" />{fmtDate(program.start_date)} – {fmtDate(program.end_date)}</span>
            )}
            {program.application_deadline && (
              <span className="inline-flex items-center gap-1.5">
                <Clock className="h-3.5 w-3.5" />
                {program.applications_open ? `Apply by ${fmtDate(program.application_deadline)}` : `Applications closed ${fmtDate(program.application_deadline)}`}
              </span>
            )}
            {program.eligible_grades?.length > 0 && (
              <span className="inline-flex items-center gap-1.5"><Users className="h-3.5 w-3.5" />{gradeList(program.eligible_grades)}</span>
            )}
          </div>
        </div>
        <Badge className={`border-0 text-[10px] ${tone}`} data-testid={`journey-stage-${program.id}`}>
          {STAGE_LABEL[stage]}
        </Badge>
      </div>

      <div className="mt-4">
        {stage === 'applications_open' && (
          <div className="rounded-xl bg-[#FFF7F5] border border-[#FBD9D2] px-4 py-3" data-testid="stage-applications-open">
            <p className="text-sm text-[#4B5563]">
              Explore the internships below and select up to two preferences for {child.child_name}.
            </p>
          </div>
        )}

        {['applied', 'under_review'].includes(stage) && application && (
          <div data-testid="stage-applied">
            <ApplicationCard application={application} onEdit={onEditApplication} />
          </div>
        )}

        {placement && (
          <div data-testid="stage-placement">
            <div className="rounded-xl bg-[#F6FCF8] border border-emerald-100 px-4 py-3 mb-3">
              <p className="text-sm font-bold text-[#1F2937] font-['Outfit']">{placement.title}</p>
              <p className="text-sm text-[#6B7280] mt-0.5">{placement.company_name}</p>
              <p className="text-sm text-[#6B7280] mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1">
                <span className="inline-flex items-center gap-1"><CalendarRange className="h-3.5 w-3.5" />
                  {fmtDate(placement.joining_instructions?.reporting_start_date || placement.start_date)} – {fmtDate(placement.joining_instructions?.reporting_end_date || placement.end_date)}
                  {placement.duration_days ? ` · ${placement.duration_days} day${placement.duration_days === 1 ? '' : 's'}` : ''}
                </span>
                {placement.workplace_address && (
                  <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{placement.workplace_address}</span>
                )}
              </p>
            </div>
            <PlacementPanel placement={placement} autoConfirm={autoConfirm} onChanged={onChanged} />
          </div>
        )}

        {stage === 'not_selected' && (
          <div className="rounded-xl bg-[#FAFBFC] border border-[#E5E7EB] px-4 py-3 flex items-start gap-2.5" data-testid="stage-not-selected">
            <Heart className="h-4 w-4 text-[#9CA3AF] mt-0.5 shrink-0" />
            <p className="text-sm text-[#4B5563] leading-relaxed">
              {child.child_name} was not placed in this programme — there were more applications than places.
              The school keeps every application on file and will let you know when the next programme opens.
            </p>
          </div>
        )}

        {stage === 'closed' && !application && (
          <p className="text-xs text-[#9CA3AF]" data-testid="stage-closed">
            Applications for this programme are closed and no application was submitted for {child.child_name}.
          </p>
        )}
      </div>
    </div>
  );
}

function HistoryRow({ row, onOpen }) {
  const p = row.placement;
  return (
    <button
      onClick={onOpen}
      className="w-full text-left rounded-xl border border-[#EFF0F2] bg-white px-4 py-3 hover:border-[#E0E2E6] transition-colors"
      data-testid={`history-row-${row.id}`}
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-[#1F2937] truncate">
            {p ? `${p.title} · ${p.company_name}` : row.name}
          </p>
          <p className="text-xs text-[#9CA3AF] mt-0.5">
            {p ? `${row.name} · ${fmtDate(p.start_date)}` : row.application_deadline
              ? `Applications closed ${fmtDate(row.application_deadline)}` : 'No application submitted'}
          </p>
        </div>
        <Badge className={`border-0 text-[10px] ${STAGE_TONE[row.stage] || 'bg-gray-100 text-gray-500'}`}>
          {STAGE_LABEL[row.stage]}
        </Badge>
      </div>
    </button>
  );
}

function OpportunityCard({ opp, child, preference, onOpen }) {
  return (
    <button
      type="button"
      onClick={onOpen}
      className={`text-left bg-white rounded-2xl border p-5 hover:shadow-sm transition-all ${preference ? 'border-[#F97D6A] ring-1 ring-[#FBD9D2]' : 'border-[#E5E7EB] hover:border-[#F97D6A]'}`}
      data-testid={`internship-card-${opp.id}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-xs font-semibold text-[#F97D6A] uppercase tracking-wider truncate">{opp.company_name}</p>
          <h3 className="text-base font-bold text-[#1F2937] font-['Outfit'] mt-1 leading-snug">{opp.title}</h3>
        </div>
        <Badge className="border-0 bg-[#FFF0ED] text-[#F97D6A] text-[10px] shrink-0">{opp.duration_days}d</Badge>
      </div>

      {(opp.career_name || (opp.related_careers || []).length > 0) && (
        <div className="flex flex-wrap items-center gap-1.5 mt-2.5" data-testid={`card-careers-${opp.id}`}>
          <Compass className="h-3.5 w-3.5 text-[#2A4B8D] shrink-0" />
          {opp.career_name && (
            <span className="text-[11px] font-semibold text-[#2A4B8D] bg-[#EEF3FF] border border-[#DCE7FB] rounded-full px-2.5 py-0.5">
              {opp.career_name}
            </span>
          )}
          {(opp.related_careers || []).map(c => (
            <span key={c.slug} className="text-[11px] font-medium text-[#5B7BBF] bg-[#F7FAFF] border border-[#E8EFFB] rounded-full px-2.5 py-0.5">
              {c.name}
            </span>
          ))}
        </div>
      )}
      {opp.description && <p className="text-sm text-[#4B5563] mt-3 line-clamp-2 leading-relaxed">{opp.description}</p>}

      <div className="mt-4 space-y-1.5 text-sm text-[#6B7280]">
        <p className="flex items-center gap-1.5"><CalendarRange className="h-3.5 w-3.5 shrink-0" />{fmtDate(opp.start_date)} – {fmtDate(opp.end_date)}</p>
        <p className="flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5 shrink-0" />{FORMAT_LABELS[opp.format] || opp.format}{opp.workplace_address ? ` · ${opp.workplace_address}` : ''}</p>
        <p className="flex items-center gap-1.5"><Users className="h-3.5 w-3.5 shrink-0" />{opp.positions_available} of {opp.positions} {opp.positions === 1 ? 'place' : 'places'} open</p>
      </div>

      <div className="flex items-center justify-end mt-4 pt-3 border-t border-[#F3F4F6]">
        {preference
          ? <span className="text-[11px] font-bold text-[#F06651]" data-testid={`opp-state-${opp.id}`}>Preference {preference}</span>
          : !opp.applications_open
            ? <span className="text-[11px] font-semibold text-[#9CA3AF]" data-testid={`opp-state-${opp.id}`}>Applications closed</span>
            : <span className="text-[11px] font-semibold text-[#6B7280]" data-testid={`opp-state-${opp.id}`}>Available</span>}
      </div>
    </button>
  );
}

function OpportunityDetails({ opp, child, preference, prefs, onSelect }) {
  const row = (opp.child_eligibility || []).find(c => c.child_id === child?.child_id);
  const label = preference ? 'Remove preference'
    : !prefs.p1 ? 'Add as Preference 1'
    : !prefs.p2 ? 'Add as Preference 2'
    : 'Replace Preference 2';

  return (
    <>
      <DialogHeader>
        <DialogTitle className="font-['Outfit'] text-lg pr-6">{opp.title}</DialogTitle>
      </DialogHeader>

      <div className="flex flex-wrap items-center gap-2 -mt-1">
        <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#F97D6A]">
          <Building2 className="h-4 w-4" />{opp.company_name}
        </span>
        {opp.industry && <Badge className="border-0 bg-gray-100 text-gray-600 text-[10px]">{opp.industry}</Badge>}
        {opp.career_name && (
          opp.career_slug ? (
            <a href={`/careers/${opp.career_slug}`} target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-1 text-[11px] font-semibold text-blue-700 bg-blue-50 rounded-full px-2.5 py-1 hover:bg-blue-100 transition-colors"
              data-testid="detail-career-link">
              {opp.career_name}<ExternalLink className="h-3 w-3" />
            </a>
          ) : <Badge className="border-0 bg-blue-50 text-blue-700 text-[10px]">{opp.career_name}</Badge>
        )}
        {(opp.related_careers || []).map(c => (
          <a key={c.slug} href={`/careers/${c.slug}`} target="_blank" rel="noreferrer"
            className="inline-flex items-center gap-1 text-[11px] text-[#6B7280] bg-[#F3F4F6] rounded-full px-2.5 py-1 hover:text-[#1F2937] transition-colors"
            data-testid={`detail-related-career-${c.slug}`}>
            {c.name}<ExternalLink className="h-3 w-3" />
          </a>
        ))}
        {preference > 0 && (
          <Badge className="border-0 bg-[#FFF0ED] text-[#F06651] text-[10px]" data-testid="detail-preference-badge">
            Preference {preference}
          </Badge>
        )}
      </div>

      <div className="grid sm:grid-cols-2 gap-3 mt-2" data-testid="internship-detail-facts">
        <Fact icon={CalendarRange} label="Dates" value={`${fmtDate(opp.start_date)} – ${fmtDate(opp.end_date)}`} />
        <Fact icon={Clock} label="Duration" value={`${opp.duration_days} ${opp.duration_days === 1 ? 'day' : 'days'}${opp.daily_start_time ? ` · ${opp.daily_start_time}–${opp.daily_end_time}` : ''}`} />
        <Fact icon={MapPin} label={FORMAT_LABELS[opp.format] || 'Location'} value={opp.workplace_address || '—'} />
        <Fact icon={Users} label="Places" value={`${opp.positions_available} of ${opp.positions} open · ${gradeList(opp.eligible_grades)}`} />
      </div>

      {opp.description && <Block title="About this internship" text={opp.description} />}
      {opp.student_activities && <Block title="What the student will do" text={opp.student_activities} />}
      {opp.learning_outcomes && <Block title="What they will learn" text={opp.learning_outcomes} />}
      {opp.prerequisites && <Block title="Prerequisites" text={opp.prerequisites} />}
      {opp.additional_instructions && <Block title="Additional instructions" text={opp.additional_instructions} />}
      {opp.supervisor_name && <Block title="Supervisor" text={opp.supervisor_name} />}

      <div className="rounded-xl bg-[#F8F9FA] p-4 mt-2 flex items-start justify-between gap-3" data-testid="internship-eligibility">
        <div>
          <p className="text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-wider">Student</p>
          <p className="text-sm font-medium text-[#1F2937] mt-1">{child?.child_name}</p>
          <p className="text-xs text-[#9CA3AF]">{child?.grade ? `Grade ${child.grade}` : 'Grade not recorded'}</p>
        </div>
        <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-600 shrink-0">
          <CheckCircle2 className="h-3.5 w-3.5" /> Eligible
        </span>
      </div>

      <div className="rounded-xl border border-[#E5E7EB] p-4 flex items-start gap-2.5" data-testid="internship-selection-note">
        <AlertCircle className="h-4 w-4 text-[#9CA3AF] mt-0.5 shrink-0" />
        <p className="text-sm text-[#6B7280] leading-relaxed">
          {opp.applications_open
            ? `You can rank up to two internships. Change or remove a preference any time before you submit, and edit the application until ${fmtDate(opp.application_deadline)}.`
            : 'Applications for this programme are closed.'}
        </p>
      </div>

      <Button
        className={`w-full rounded-xl ${preference ? 'bg-white border border-[#E5E7EB] text-[#1F2937] hover:bg-[#F8F9FA]' : 'bg-[#F97D6A] hover:bg-[#F06651] text-white'}`}
        disabled={!opp.applications_open || !row?.eligible}
        onClick={onSelect}
        data-testid="internship-select-btn"
      >
        {!opp.applications_open ? 'Applications closed' : label}
      </Button>
    </>
  );
}

function PreferenceSummary({ prefs, opportunities, onOpen, onRemove, onContinue }) {
  const byId = (id) => opportunities.find(o => o.id === id);
  const first = byId(prefs.p1);
  const second = byId(prefs.p2);
  const onlyOne = opportunities.length < 2;

  return (
    <div className="rounded-2xl border border-[#FBD9D2] bg-[#FFF7F5] p-4 mb-4" data-testid="preference-summary">
      <p className="text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-wider">Your internship preferences</p>
      <div className="mt-2.5 space-y-2">
        <PrefRow
          slot={1} title={first ? `${first.title} · ${first.company_name}` : ''}
          onChange={() => onOpen(prefs.p1)} onRemove={() => onRemove('p1')}
        />
        {second ? (
          <PrefRow
            slot={2} title={`${second.title} · ${second.company_name}`}
            onChange={() => onOpen(prefs.p2)} onRemove={() => onRemove('p2')}
          />
        ) : (
          <div className="flex items-center gap-2.5 text-xs text-[#9CA3AF]" data-testid="preference-row-2-empty">
            <span className="h-5 w-5 rounded-full bg-white text-[#9CA3AF] text-[10px] font-bold flex items-center justify-center shrink-0">2</span>
            {onlyOne
              ? 'Optional — no other internship is open to this student in this programme'
              : 'Optional — open any internship below to add a second preference'}
          </div>
        )}
      </div>
      <Button
        className="w-full mt-3.5 rounded-xl bg-[#F97D6A] hover:bg-[#F06651] text-white font-semibold"
        onClick={onContinue}
        data-testid="continue-to-application-btn"
      >
        Continue to application
      </Button>
    </div>
  );
}

const PrefRow = ({ slot, title, onChange, onRemove }) => (
  <div className="flex items-center gap-2.5" data-testid={`preference-row-${slot}`}>
    <span className="h-5 w-5 rounded-full bg-[#F97D6A] text-white text-[10px] font-bold flex items-center justify-center shrink-0">{slot}</span>
    <p className="text-xs font-medium text-[#1F2937] truncate flex-1">{title}</p>
    <button onClick={onChange} className="text-[11px] text-[#6B7280] hover:text-[#1F2937] underline shrink-0" data-testid={`preference-change-${slot}`}>Change</button>
    <button onClick={onRemove} className="text-[11px] text-[#9CA3AF] hover:text-[#F06651] underline shrink-0" data-testid={`preference-remove-${slot}`}>Remove</button>
  </div>
);

const Fact = ({ icon: Icon, label, value }) => (
  <div className="bg-[#F8F9FA] rounded-xl px-4 py-3">
    <p className="text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-wider flex items-center gap-1.5">
      <Icon className="h-3.5 w-3.5" />{label}
    </p>
    <p className="text-sm text-[#1F2937] mt-1 leading-snug">{value}</p>
  </div>
);

const Block = ({ title, text }) => (
  <div className="mt-1">
    <p className="text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-wider">{title}</p>
    <p className="text-sm text-[#4B5563] mt-1 leading-relaxed whitespace-pre-line">{text}</p>
  </div>
);
